import { RDCashCrop, RDCoverCrop, RDTillage } from '@cibo/core'
import { useFigures } from '@cibo/ui'
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../context'
import { useEqipEngagementFieldsWithConcerns } from '../../queries/useEqipRecommendations'
import { PdfLoadingIndicator } from './PdfLoadingIndicator'

type FieldHistorySectionProps = {
  engagementId: string
}

const HISTORY_YEARS = 5

export const FieldHistorySection = ({ engagementId }: FieldHistorySectionProps) => {
  const { t } = useTranslation('@cibo/programs/EqipSelectedPracticesPDF')
  const { t: cropT } = useTranslation('@cibo/ui/cropNames')
  const { acres } = useFigures()

  const { engagement } = useProgramEngagementContext()
  const fieldModels = useEqipEngagementFieldsWithConcerns(engagementId)

  const programYear = engagement.data?.programYear ?? new Date().getFullYear()

  const { sortedYears, fieldHistoryData } = useMemo(() => {
    if (!fieldModels.data?.length) return { sortedYears: [], fieldHistoryData: [] }

    const allYears = new Set<number>()
    const fieldHistoryData = fieldModels.data.map(field => {
      const cashCrops = field.details.filter(
        d => d.traitId === 'cashCrop' && d.year && d.year < programYear
      ) as RDCashCrop[]
      const tillage = field.details.filter(
        d => d.traitId === 'tillage' && d.year && d.year < programYear
      ) as RDTillage[]
      const coverCrops = field.details.filter(
        d => d.traitId === 'coverCrop' && d.year && d.year < programYear
      ) as RDCoverCrop[]

      cashCrops.forEach(d => allYears.add(d.year))
      tillage.forEach(d => allYears.add(d.year))
      coverCrops.forEach(d => allYears.add(d.year))

      return {
        name: field.name || '—',
        acres: field.acres,
        cashCrops,
        tillage,
        coverCrops,
      }
    })

    const sortedYears = [...allYears].sort((a, b) => b - a).slice(0, HISTORY_YEARS)
    return { sortedYears, fieldHistoryData }
  }, [fieldModels.data, programYear])

  if (fieldModels.isPending || !fieldModels.isFetched) return <PdfLoadingIndicator />

  if (!sortedYears.length) return <div data-section="field-history" />

  const colCount = sortedYears.length + 1

  return (
    <div style={{ pageBreakBefore: 'always', paddingTop: 24 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('fieldHistoryTitle')}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('fieldHistoryFieldName')}</TableCell>
            {sortedYears.map(year => (
              <TableCell key={year} align="center">
                {year}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Cash Crop section */}
          <TableRow>
            <TableCell
              colSpan={colCount}
              sx={{ fontWeight: 'bold', bgcolor: 'action.hover', py: 0.5 }}
            >
              {t('fieldHistoryCashCropHeading')}
            </TableCell>
          </TableRow>
          {fieldHistoryData.map((field, idx) => (
            <TableRow key={`cash-${idx}`}>
              <TableCell>
                {field.name}
                <Typography variant="caption" display="block" color="text.secondary">
                  {field.acres ? acres(field.acres) : ''}
                </Typography>
              </TableCell>
              {sortedYears.map(year => {
                const detail = field.cashCrops.find(c => c.year === year)
                const crop = detail?.result ?? detail?.input?.crop
                return (
                  <TableCell key={year} align="center">
                    {crop ? cropT(crop) : '—'}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}

          {/* Tillage section */}
          <TableRow>
            <TableCell
              colSpan={colCount}
              sx={{ fontWeight: 'bold', bgcolor: 'action.hover', py: 0.5 }}
            >
              {t('fieldHistoryTillageHeading')}
            </TableCell>
          </TableRow>
          {fieldHistoryData.map((field, idx) => (
            <TableRow key={`till-${idx}`}>
              <TableCell>
                {field.name}
                <Typography variant="caption" display="block" color="text.secondary">
                  {field.acres ? acres(field.acres) : ''}
                </Typography>
              </TableCell>
              {sortedYears.map(year => {
                const detail = field.tillage.find(c => c.year === year)
                return (
                  <TableCell key={year} align="center">
                    {detail?.result ? t('tillage', { context: detail.result }) : '—'}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}

          {/* Cover Crop section */}
          <TableRow>
            <TableCell
              colSpan={colCount}
              sx={{ fontWeight: 'bold', bgcolor: 'action.hover', py: 0.5 }}
            >
              {t('fieldHistoryCoverCropHeading')}
            </TableCell>
          </TableRow>
          {fieldHistoryData.map((field, idx) => (
            <TableRow key={`cover-${idx}`}>
              <TableCell>
                {field.name}
                <Typography variant="caption" display="block" color="text.secondary">
                  {field.acres ? acres(field.acres) : ''}
                </Typography>
              </TableCell>
              {sortedYears.map(year => {
                const detail = field.coverCrops.find(c => c.year === year)
                const crop = detail?.result ? detail.input?.crop ?? t('yes') : undefined
                return (
                  <TableCell key={year} align="center">
                    {crop ? cropT(crop) : '—'}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
